import React, { useState, useMemo } from "react";
import { useCollection, api, LEAD_STAGES, leadStageColor, RELATIONSHIP_STRENGTH, fmtMoney, fmtDate } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, Pill, Labeled } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Phone, Mail, Edit, Trash2, ArrowRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";

const personEmpty = { name: "", organization: "", role_title: "", phone: "", email: "", city: "", categories: "", relationship_strength: 1, strategic_importance: 3, last_interaction: "", next_interaction: "", owner: "", what_they_care_about: "", how_we_help: "", what_they_help: "", notes: "" };
const leadEmpty = { name: "", contact: "", product: "", source: "", estimated_value: 0, stage: "new", last_contact: "", next_contact: "", owner: "", notes: "" };
const custEmpty = { name: "", service: "", amount_sold: 0, amount_paid: 0, start_date: "", completion_date: "", next_action: "", dog_name: "", breed: "", age: "", program: "", notes: "" };

export default function People() {
  const [tab, setTab] = useState("people");
  return (
    <div className="space-y-6">
      <PageHeader title="People" subtitle="Relationships, leads, and customers — one command database" testId="people-header" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-surface border border-edge">
          <TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Sales Pipeline</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
        </TabsList>
        <TabsContent value="people" className="mt-5"><PeopleTab /></TabsContent>
        <TabsContent value="leads" className="mt-5"><LeadsTab /></TabsContent>
        <TabsContent value="customers" className="mt-5"><CustomersTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PeopleTab() {
  const { items, loading, create, update, remove } = useCollection("people");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(personEmpty);
  const [editId, setEditId] = useState(null);
  const [flt, setFlt] = useState(null);

  const filtered = useMemo(() => {
    const now = Date.now();
    return items.filter((p) => {
      if (!flt) return true;
      if (flt === "call_today") return p.next_interaction && new Date(p.next_interaction).getTime() <= now + 864e5;
      if (flt === "cold") return p.last_interaction && (now - new Date(p.last_interaction).getTime()) > 21 * 864e5;
      if (flt === "strong") return Number(p.relationship_strength) >= 4;
      if (flt === "new") return Number(p.relationship_strength) === 1;
      return true;
    });
  }, [items, flt]);

  const openNew = () => { setForm(personEmpty); setEditId(null); setDlg(true); };
  const openEdit = (p) => { setForm({ ...personEmpty, ...p }); setEditId(p.id); setDlg(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, relationship_strength: Number(form.relationship_strength), strategic_importance: Number(form.strategic_importance) };
    if (editId) await update(editId, payload); else await create(payload);
    toast.success("Saved"); setDlg(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-2">
          {[{ k: null, l: "All" }, { k: "call_today", l: "Call Today" }, { k: "cold", l: "Going Cold" }, { k: "strong", l: "Strategic Allies" }, { k: "new", l: "New" }].map((f) => (
            <button key={f.l} onClick={() => setFlt(f.k)} className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border", flt === f.k ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3]")}>{f.l}</button>
          ))}
        </div>
        <button data-testid="person-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm"><Plus size={15} /> Add Person</button>
      </div>

      {filtered.length === 0 ? <EmptyState title="No contacts yet." hint="Add the people who matter to the operation." action={<button onClick={openNew} className="px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm">+ Add Person</button>} /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`person-${p.id}`} className="bg-surface border border-edge rounded-xl p-4 hover:border-gold/30 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-sm text-[#A3A3A3] truncate">{[p.role_title, p.organization].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(p)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={14} /></button>
                  <button onClick={async () => { await remove(p.id); toast.success("Archived"); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Pill color="#D4AF37"><Star size={11} /> {RELATIONSHIP_STRENGTH[p.relationship_strength] || "New"}</Pill>
                {p.next_interaction && <Pill color="#F59E0B">next {fmtDate(p.next_interaction)}</Pill>}
              </div>
              <div className="flex gap-2 mt-3">
                {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><Phone size={12} /> Call</a>}
                {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><Mail size={12} /> Email</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit Person" : "Add Person"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Name"><Input data-testid="person-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Organization"><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Role / Title"><Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Relationship Strength">
                <select value={form.relationship_strength} onChange={(e) => setForm({ ...form, relationship_strength: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{Object.entries(RELATIONSHIP_STRENGTH).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}</select>
              </Labeled>
              <Labeled label="Strategic Importance (1-5)"><Input type="number" min="1" max="5" value={form.strategic_importance} onChange={(e) => setForm({ ...form, strategic_importance: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Last Interaction"><Input type="date" value={(form.last_interaction || "").slice(0, 10)} onChange={(e) => setForm({ ...form, last_interaction: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Next Interaction"><Input type="date" value={(form.next_interaction || "").slice(0, 10)} onChange={(e) => setForm({ ...form, next_interaction: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="What they care about"><Input value={form.what_they_care_about} onChange={(e) => setForm({ ...form, what_they_care_about: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="How we can help them"><Input value={form.how_we_help} onChange={(e) => setForm({ ...form, how_we_help: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="person-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadsTab() {
  const { items, loading, create, update, remove, load } = useCollection("leads");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(leadEmpty);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm(leadEmpty); setEditId(null); setDlg(true); };
  const openEdit = (l) => { setForm({ ...leadEmpty, ...l }); setEditId(l.id); setDlg(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
    if (editId) await update(editId, payload); else { payload.date_entered = new Date().toISOString(); await create(payload); }
    toast.success("Saved"); setDlg(false);
  };
  const setStage = async (l, stage) => { await update(l.id, { stage, last_contact: new Date().toISOString() }); toast.success(`→ ${stage.toUpperCase()}`); };
  const convert = async (l) => { await api.post(`/leads/${l.id}/convert`); toast.success("Converted to customer"); load(); };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button data-testid="lead-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm"><Plus size={15} /> Add Lead</button>
      </div>
      {items.length === 0 ? <EmptyState title="No leads yet." hint="Add a lead to start the pipeline." action={<button onClick={openNew} className="px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm">+ Add Lead</button>} /> : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} data-testid={`lead-${l.id}`} className="bg-surface border border-edge rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{l.name}</p>
                  <p className="text-sm text-[#A3A3A3]">{l.product || "—"} {l.estimated_value ? `· ${fmtMoney(l.estimated_value)}` : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0"><button onClick={() => openEdit(l)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={14} /></button><button onClick={async () => { await remove(l.id); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={14} /></button></div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {LEAD_STAGES.map((s) => (
                  <button key={s.key} data-testid={`lead-stage-${l.id}-${s.key}`} onClick={() => setStage(l, s.key)}
                    className={cn("px-2.5 py-1 rounded-full text-[0.65rem] font-bold border transition-colors", l.stage === s.key ? "text-ink border-transparent" : "border-edge text-[#A3A3A3] hover:text-[#FAFAFA]")}
                    style={l.stage === s.key ? { backgroundColor: leadStageColor(s.key) } : {}}>{s.label}</button>
                ))}
              </div>
              {l.stage === "won" && <button data-testid={`lead-convert-${l.id}`} onClick={() => convert(l)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sgreen text-ink text-xs font-bold"><ArrowRight size={13} /> Convert to Customer</button>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit Lead" : "Add Lead"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Name"><Input data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Contact"><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Product / Service"><Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Source"><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Estimated Value ($)"><Input data-testid="lead-value" type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Stage"><select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{LEAD_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></Labeled>
              <Labeled label="Next Contact"><Input type="date" value={(form.next_contact || "").slice(0, 10)} onChange={(e) => setForm({ ...form, next_contact: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="lead-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save Lead</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomersTab() {
  const { items, loading, create, update, remove } = useCollection("customers");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(custEmpty);
  const [editId, setEditId] = useState(null);
  const [showDog, setShowDog] = useState(false);

  const openNew = () => { setForm(custEmpty); setEditId(null); setShowDog(false); setDlg(true); };
  const openEdit = (c) => { setForm({ ...custEmpty, ...c }); setEditId(c.id); setShowDog(!!(c.dog_name || c.breed)); setDlg(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, amount_sold: Number(form.amount_sold) || 0, amount_paid: Number(form.amount_paid) || 0 };
    if (editId) await update(editId, payload); else await create(payload);
    toast.success("Saved"); setDlg(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button data-testid="customer-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm"><Plus size={15} /> Add Customer</button></div>
      {items.length === 0 ? <EmptyState title="No customers yet." hint="Won leads convert into customers here." /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((c) => {
            const bal = (c.amount_sold || 0) - (c.amount_paid || 0);
            return (
              <div key={c.id} data-testid={`customer-${c.id}`} className="bg-surface border border-edge rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><p className="font-semibold truncate">{c.name}</p><p className="text-sm text-[#A3A3A3] truncate">{c.service || "—"}{c.dog_name ? ` · ${c.dog_name}` : ""}</p></div>
                  <div className="flex gap-1 shrink-0"><button onClick={() => openEdit(c)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={14} /></button><button onClick={async () => { await remove(c.id); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={14} /></button></div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Pill color="#10B981">Paid {fmtMoney(c.amount_paid)}</Pill>
                  {bal > 0 && <Pill color="#F59E0B">Balance {fmtMoney(bal)}</Pill>}
                </div>
                {c.next_action && <p className="text-sm mt-2"><span className="label-caps">Next: </span>{c.next_action}</p>}
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Name"><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Service"><Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Amount Sold ($)"><Input type="number" value={form.amount_sold} onChange={(e) => setForm({ ...form, amount_sold: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Amount Paid ($)"><Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Start Date"><Input type="date" value={(form.start_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Completion Date"><Input type="date" value={(form.completion_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="Next Action"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button onClick={() => setShowDog(!showDog)} className="text-xs label-caps text-gold">{showDog ? "− Hide" : "+ Add"} dog-training fields</button>
            {showDog && (
              <div className="grid grid-cols-2 gap-3 animate-fade-up">
                <Labeled label="Dog Name"><Input value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })} className="bg-ink border-edge" /></Labeled>
                <Labeled label="Breed"><Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} className="bg-ink border-edge" /></Labeled>
                <Labeled label="Age"><Input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="bg-ink border-edge" /></Labeled>
                <Labeled label="Program"><Input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className="bg-ink border-edge" /></Labeled>
              </div>
            )}
            <Labeled label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="customer-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save Customer</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
